import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import {
  Dumbbell, Wind, ShieldCheck, Heart, ChevronLeft, Copy, Trash2, ChevronDown, ChevronUp,
} from 'lucide-react';
import { toast } from 'sonner';

const CAT_COLORS = {
  STRENGTH: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  AEROBIC: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  RTP: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  RECOVERY: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
};
const CAT_ICONS = { STRENGTH: Dumbbell, AEROBIC: Wind, RTP: ShieldCheck, RECOVERY: Heart };

export default function PrescriptionTemplatesPage() {
  const { api, hasRole } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const canEdit = hasRole(['ADMIN', 'SNC', 'PHYSIO']);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get('/prescriptions/templates');
      setTemplates(res.data);
    } catch (e) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleUseTemplate = (tpl) => {
    navigate('/staff/prescriptions/builder', { state: { template: tpl } });
  };

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await api.delete(`/prescriptions/templates/${tpl.id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6" data-testid="prescription-templates-page">
      <div className="flex items-center gap-3 mb-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/staff/prescriptions')} className="text-zinc-400 hover:text-zinc-100" data-testid="back-to-prescriptions">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
      </div>
      <div>
        <h1 className="text-2xl font-bold text-zinc-100" data-testid="templates-title">Prescription Templates</h1>
        <p className="text-sm text-zinc-500 mt-1">{templates.length} templates available</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="text-zinc-500">Loading...</div></div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">No templates found</div>
      ) : (
        <div className="space-y-3" data-testid="templates-list">
          {templates.map(tpl => {
            const Icon = CAT_ICONS[tpl.category] || Dumbbell;
            const isExpanded = expanded === tpl.id;
            return (
              <Card key={tpl.id} className="bg-zinc-900/50 border-zinc-800" data-testid={`template-card-${tpl.id}`}>
                <CardContent className="p-0">
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-zinc-800/30 transition-colors"
                    onClick={() => setExpanded(isExpanded ? null : tpl.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-sm ${CAT_COLORS[tpl.category]}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-zinc-100">{tpl.name}</h3>
                        <p className="text-xs text-zinc-500 mt-0.5">{tpl.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`${CAT_COLORS[tpl.category]} text-xs`}>{tpl.category}</Badge>
                      <Badge variant="outline" className="border-zinc-700 text-zinc-500 text-xs">
                        {(tpl.blocks || []).length} block{(tpl.blocks || []).length !== 1 ? 's' : ''}
                      </Badge>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-zinc-800 p-4 space-y-3">
                      {(tpl.blocks || []).map((block, bi) => (
                        <div key={bi} className="bg-zinc-800/30 rounded-sm p-3">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-semibold text-zinc-300">{block.name}</span>
                            <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-500">{block.block_type}</Badge>
                          </div>
                          <div className="space-y-1.5">
                            {(block.items || []).map((item, ii) => (
                              <div key={ii} className="flex items-center justify-between text-xs">
                                <span className="text-zinc-300">{item.exercise_name || 'Exercise'}</span>
                                <div className="flex gap-3 text-zinc-500">
                                  {item.sets && <span>{item.sets} sets</span>}
                                  {item.reps && <span>{item.reps} reps</span>}
                                  {item.duration_sec && <span>{Math.round(item.duration_sec / 60)}min</span>}
                                  {item.params?.work_sec && <span>{item.params.work_sec}s/{item.params.rest_sec}s</span>}
                                  {item.params?.intensity_pct && <span>{item.params.intensity_pct}%</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                          {block.notes && <p className="text-[11px] text-zinc-600 mt-2 italic">{block.notes}</p>}
                        </div>
                      ))}
                      <div className="flex justify-end gap-2 pt-2">
                        {canEdit && hasRole(['ADMIN', 'SNC']) && (
                          <Button variant="outline" size="sm" onClick={() => handleDelete(tpl)} className="border-zinc-700 text-rose-400 hover:bg-rose-500/10" data-testid={`delete-template-${tpl.id}`}>
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                          </Button>
                        )}
                        {canEdit && (
                          <Button size="sm" onClick={() => handleUseTemplate(tpl)} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid={`use-template-${tpl.id}`}>
                            <Copy className="w-3.5 h-3.5 mr-1" /> Use Template
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
